import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  EmojiEvents as RewardIcon,
  Stars as StarsIcon,
  CardGiftcard as GiftIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

const RewardSettings = () => {
  const [tabValue, setTabValue] = useState(0);

  const [pointsSettings, setPointsSettings] = useState({
    pointsPerRupee: 1,
    minRedemptionPoints: 100,
    maxRedemptionPoints: 100000,
    pointsExpiryDays: 365,
    enablePointsExpiry: true,
  });

  const [earningRules, setEarningRules] = useState({
    purchasePoints: true,
    purchasePointsRate: 1,
    referralPoints: true,
    referralPointsAmount: 500,
    signupPoints: true,
    signupPointsAmount: 100,
  });

  const [redemptionRules, setRedemptionRules] = useState({
    minOrderValue: 1000,
    allowPartialRedemption: true,
    maxRedemptionPercentage: 50,
    requireMinimumBalance: true,
  });

  const [notifications, setNotifications] = useState({
    notifyOnPointsExpiry: true,
    expiryNotificationDays: 30,
    notifyOnRedemption: true,
    notifyOnEarning: false,
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const handlePointsSettingsChange = (field, value) => {
    setPointsSettings((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleEarningRulesChange = (field, value) => {
    setEarningRules((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleRedemptionRulesChange = (field, value) => {
    setRedemptionRules((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleNotificationsChange = (field, value) => {
    setNotifications((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const e = {};
    if (pointsSettings.pointsPerRupee <= 0)
      e.pointsPerRupee = 'Points per rupee must be greater than 0';
    if (pointsSettings.minRedemptionPoints < 0)
      e.minRedemptionPoints = 'Minimum redemption points cannot be negative';
    if (pointsSettings.maxRedemptionPoints <= pointsSettings.minRedemptionPoints)
      e.maxRedemptionPoints = 'Maximum must be greater than minimum';
    if (pointsSettings.pointsExpiryDays < 0)
      e.pointsExpiryDays = 'Expiry days cannot be negative';
    if (redemptionRules.minOrderValue < 0)
      e.minOrderValue = 'Minimum order value cannot be negative';
    if (redemptionRules.maxRedemptionPercentage < 0 || redemptionRules.maxRedemptionPercentage > 100)
      e.maxRedemptionPercentage = 'Percentage must be between 0 and 100';
    return e;
  };

  const handleSave = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    // TODO: Save to API
    // const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    // await fetch(`${BASE_URL}/api/reward-settings`, {
    //   method: 'PUT',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     pointsSettings,
    //     earningRules,
    //     redemptionRules,
    //     notifications,
    //   }),
    // });

    setSuccessMessage('Settings saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Reward Settings
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{ textTransform: 'none' }}
        >
          Save Settings
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ boxShadow: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<StarsIcon />} iconPosition="start" label="Points Settings" />
          <Tab icon={<GiftIcon />} iconPosition="start" label="Earning Rules" />
          <Tab icon={<RewardIcon />} iconPosition="start" label="Redemption Rules" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Notifications" />
        </Tabs>

        {/* Points Settings Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
              Points Configuration
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Points per Rupee"
                  type="number"
                  value={pointsSettings.pointsPerRupee}
                  onChange={(e) => handlePointsSettingsChange('pointsPerRupee', parseFloat(e.target.value))}
                  error={!!errors.pointsPerRupee}
                  helperText={errors.pointsPerRupee}
                  InputProps={{
                    startAdornment: <StarsIcon sx={{ fontSize: 18, mr: 1, color: '#f57c00' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Redemption Points"
                  type="number"
                  value={pointsSettings.minRedemptionPoints}
                  onChange={(e) => handlePointsSettingsChange('minRedemptionPoints', parseInt(e.target.value))}
                  error={!!errors.minRedemptionPoints}
                  helperText={errors.minRedemptionPoints}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Maximum Redemption Points"
                  type="number"
                  value={pointsSettings.maxRedemptionPoints}
                  onChange={(e) => handlePointsSettingsChange('maxRedemptionPoints', parseInt(e.target.value))}
                  error={!!errors.maxRedemptionPoints}
                  helperText={errors.maxRedemptionPoints}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Points Expiry (Days)"
                  type="number"
                  value={pointsSettings.pointsExpiryDays}
                  onChange={(e) => handlePointsSettingsChange('pointsExpiryDays', parseInt(e.target.value))}
                  error={!!errors.pointsExpiryDays}
                  helperText={errors.pointsExpiryDays}
                  disabled={!pointsSettings.enablePointsExpiry}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={pointsSettings.enablePointsExpiry}
                      onChange={(e) =>
                        handlePointsSettingsChange('enablePointsExpiry', e.target.checked)
                      }
                    />
                  }
                  label="Enable Points Expiry"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Earning Rules Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
              Earning Rules Configuration
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={earningRules.purchasePoints}
                      onChange={(e) => handleEarningRulesChange('purchasePoints', e.target.checked)}
                    />
                  }
                  label="Enable Points on Purchase"
                />
              </Grid>

              {earningRules.purchasePoints && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Points per Rupee (Purchase)"
                    type="number"
                    value={earningRules.purchasePointsRate}
                    onChange={(e) =>
                      handleEarningRulesChange('purchasePointsRate', parseFloat(e.target.value))
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={earningRules.referralPoints}
                      onChange={(e) => handleEarningRulesChange('referralPoints', e.target.checked)}
                    />
                  }
                  label="Enable Referral Bonus Points"
                />
              </Grid>

              {earningRules.referralPoints && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Referral Bonus Points"
                    type="number"
                    value={earningRules.referralPointsAmount}
                    onChange={(e) =>
                      handleEarningRulesChange('referralPointsAmount', parseInt(e.target.value))
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={earningRules.signupPoints}
                      onChange={(e) => handleEarningRulesChange('signupPoints', e.target.checked)}
                    />
                  }
                  label="Enable Sign-up Bonus Points"
                />
              </Grid>

              {earningRules.signupPoints && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Sign-up Bonus Points"
                    type="number"
                    value={earningRules.signupPointsAmount}
                    onChange={(e) =>
                      handleEarningRulesChange('signupPointsAmount', parseInt(e.target.value))
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Redemption Rules Tab */}
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
              Redemption Rules Configuration
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minimum Order Value (₹)"
                  type="number"
                  value={redemptionRules.minOrderValue}
                  onChange={(e) =>
                    handleRedemptionRulesChange('minOrderValue', parseFloat(e.target.value))
                  }
                  error={!!errors.minOrderValue}
                  helperText={errors.minOrderValue}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={redemptionRules.allowPartialRedemption}
                      onChange={(e) =>
                        handleRedemptionRulesChange('allowPartialRedemption', e.target.checked)
                      }
                    />
                  }
                  label="Allow Partial Redemption"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Maximum Redemption Percentage (%)"
                  type="number"
                  value={redemptionRules.maxRedemptionPercentage}
                  onChange={(e) =>
                    handleRedemptionRulesChange('maxRedemptionPercentage', parseFloat(e.target.value))
                  }
                  error={!!errors.maxRedemptionPercentage}
                  helperText={errors.maxRedemptionPercentage}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={redemptionRules.requireMinimumBalance}
                      onChange={(e) =>
                        handleRedemptionRulesChange('requireMinimumBalance', e.target.checked)
                      }
                    />
                  }
                  label="Require Minimum Balance for Redemption"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Notifications Tab */}
        {tabValue === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
              Notification Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.notifyOnPointsExpiry}
                      onChange={(e) =>
                        handleNotificationsChange('notifyOnPointsExpiry', e.target.checked)
                      }
                    />
                  }
                  label="Notify Customers on Points Expiry"
                />
              </Grid>

              {notifications.notifyOnPointsExpiry && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Notification Days Before Expiry"
                    type="number"
                    value={notifications.expiryNotificationDays}
                    onChange={(e) =>
                      handleNotificationsChange('expiryNotificationDays', parseInt(e.target.value))
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.notifyOnRedemption}
                      onChange={(e) =>
                        handleNotificationsChange('notifyOnRedemption', e.target.checked)
                      }
                    />
                  }
                  label="Notify Customers on Redemption"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.notifyOnEarning}
                      onChange={(e) =>
                        handleNotificationsChange('notifyOnEarning', e.target.checked)
                      }
                    />
                  }
                  label="Notify Customers on Points Earning"
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RewardSettings;

